import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '../store'

Vue.use(VueRouter)

const ifAuthenticated = (to, from, next) => {
  if (store.getters.isAuthenticated) {
    next()
    return
  }
  next('/login')
}

const normalizeRoles = (roles) => {
  if (!Array.isArray(roles)) {
    return []
  }

  return roles
    .map(role => {
      if (typeof role === 'string') {
        return role.toLowerCase()
      }
      if (role && typeof role.name === 'string') {
        return role.name.toLowerCase()
      }
      return ''
    })
    .filter(Boolean)
}

const ensureRolesLoaded = () => {
  if (normalizeRoles(store.getters.roles).length > 0) {
    return Promise.resolve()
  }

  return store.dispatch('FETCH_ROLES')
}

const requireRoles = (allowedRoles) => {
  return (to, from, next) => {
    ensureRolesLoaded().then(() => {
      const roles = normalizeRoles(store.getters.roles)

      if (roles.some(role => allowedRoles.includes(role))) {
        next()
        return
      }

      next({ name: 'app-dashboard' })
    }).catch(() => {
      next({ name: 'app-dashboard' })
    })
  }
}

const routes = [
  {
    path: '*',
    redirect: { name: 'app-dashboard' }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/Auth/Login.vue')
  },
  {
    path: '/password/:token',
    name: 'create-password',
    component: () => import('@/pages/Auth/CreatePassword.vue')
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/Auth/ForgotPassword.vue')
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/pages/Auth/ResetPassword.vue')
  },
  {
    path: '/validate-account/:token',
    name: 'validate-account',
    component: () => import('@/pages/Auth/ValidateAccount.vue')
  },
  {
    path: '/app',
    component: () => import('@/views/Layout.vue'),
    beforeEnter: ifAuthenticated,
    children: [
      {
        path: '/app/me',
        name: 'app-profile',
        alias: 'profile',
        component: () => import('@/pages/Profile.vue')
      },
      {
        path: '/app/dashboard',
        name: 'app-dashboard',
        alias: '/dashboard',
        component: () => import('@/pages/Dashboard.vue')
      },
      {
        path: '/app/dashboard/tracking',
        name: 'app-dashboard-tracking',
        component: () => import('@/pages/dashboard/Tracking.vue'),
        children: [
          {
            path: '',
            redirect: { name: 'app-dashboard-tracking-employees-general' }
          },
          {
            path: 'elected',
            component: () => import('@/pages/dashboard/TrackingElected.vue'),
            children: [
              {
                path: '',
                redirect: { name: 'app-dashboard-tracking-elected-connections' }
              },
              {
                path: 'connections',
                name: 'app-dashboard-tracking-elected-connections',
                alias: '/dashboard/tracking/elected/connections',
                component: () => import('@/pages/dashboard/ConnectionStats.vue')
              },
              {
                path: 'email-campaigns',
                name: 'app-dashboard-tracking-elected-email-campaigns',
                alias: '/dashboard/tracking/elected/email-campaigns',
                component: () => import('@/pages/dashboard/EmailCampaignTracking.vue')
              },
              {
                path: 'domains-without-connections',
                name: 'app-dashboard-tracking-elected-domains-without-connections',
                alias: '/dashboard/tracking/elected/domains-without-connections',
                component: () => import('@/pages/dashboard/ConnectionInactiveDomains.vue')
              },
              {
                path: 'published-contents',
                name: 'app-dashboard-tracking-elected-published-contents',
                alias: '/dashboard/tracking/elected/published-contents',
                component: () => import('@/pages/dashboard/ElectedPublishedContent.vue')
              }
            ]
          },
          {
            path: 'employees',
            component: () => import('@/pages/dashboard/TrackingEmployees.vue'),
            children: [
              {
                path: '',
                redirect: { name: 'app-dashboard-tracking-employees-general' }
              },
              {
                path: 'general',
                name: 'app-dashboard-tracking-employees-general',
                alias: '/dashboard/tracking/employees/general',
                component: () => import('@/pages/dashboard/EmployeeActivityGeneral.vue')
              },
              {
                path: 'contents',
                name: 'app-dashboard-tracking-employees-contents',
                alias: '/dashboard/tracking/employees/contents',
                component: () => import('@/pages/dashboard/ReadTracking.vue')
              },
              {
                path: 'concerns',
                name: 'app-dashboard-tracking-employees-concerns',
                alias: '/dashboard/tracking/employees/concerns',
                beforeEnter: requireRoles(['admin', 'community-manager']),
                component: () => import('@/pages/dashboard/EmployeeConcerns.vue')
              },
              {
                path: 'platforms',
                name: 'app-dashboard-tracking-employees-platforms',
                alias: '/dashboard/tracking/employees/platforms',
                component: () => import('@/pages/dashboard/EmployeePlatforms.vue')
              }
            ]
          },
          {
            path: 'generic-contents',
            component: () => import('@/pages/dashboard/GenericContents.vue'),
            beforeEnter: requireRoles(['admin', 'community-manager']),
            children: [
              {
                path: '',
                redirect: { name: 'app-dashboard-tracking-generic-contents-media' }
              },
              {
                path: 'media',
                name: 'app-dashboard-tracking-generic-contents-media',
                alias: '/dashboard/tracking/generic-contents/media',
                component: () => import('@/pages/dashboard/GenericMedia.vue')
              },
              {
                path: 'faq',
                component: () => import('@/pages/dashboard/GenericFaq.vue'),
                children: [
                  {
                    path: '',
                    redirect: { name: 'app-dashboard-tracking-generic-contents-faq-questions' }
                  },
                  {
                    path: 'questions',
                    name: 'app-dashboard-tracking-generic-contents-faq-questions',
                    alias: '/dashboard/tracking/generic-contents/faq/questions',
                    component: () => import('@/pages/dashboard/GenericFaqQuestions.vue')
                  },
                  {
                    path: 'domains',
                    name: 'app-dashboard-tracking-generic-contents-faq-domains',
                    alias: '/dashboard/tracking/generic-contents/faq/domains',
                    component: () => import('@/pages/dashboard/GenericFaqDomains.vue')
                  }
                ]
              }
            ]
          },
          {
            path: 'ai',
            name: 'app-dashboard-tracking-ai',
            alias: '/dashboard/tracking/ai',
            beforeEnter: requireRoles(['admin', 'community-manager']),
            component: () => import('@/pages/Ai/Home.vue'),
            children: [
              {
                path: '',
                redirect: { name: 'app-dashboard-tracking-ai-tracking' }
              },
              {
                path: 'knowledge',
                name: 'app-dashboard-tracking-ai-knowledge',
                alias: '/dashboard/tracking/ai/knowledge',
                component: () => import('@/pages/Ai/Knowledge.vue')
              },
              {
                path: 'tracking',
                name: 'app-dashboard-tracking-ai-tracking',
                alias: '/dashboard/tracking/ai/tracking',
                component: () => import('@/pages/Ai/Tracking.vue')
              },
              {
                path: 'quotas',
                name: 'app-dashboard-tracking-ai-quotas',
                alias: '/dashboard/tracking/ai/quotas',
                component: () => import('@/pages/Ai/Quotas.vue')
              },
              {
                path: 'assistant-test',
                name: 'app-dashboard-tracking-ai-assistant-test',
                alias: '/dashboard/tracking/ai/assistant-test',
                component: () => import('@/pages/Ai/Agent.vue')
              }
            ]
          },
          {
            path: 'management',
            component: () => import('@/pages/dashboard/TrackingManagement.vue'),
            beforeEnter: requireRoles(['admin', 'community-manager']),
            children: [
              {
                path: '',
                redirect: { name: 'app-dashboard-tracking-management-messaging' }
              },
              {
                path: 'messaging',
                name: 'app-dashboard-tracking-management-messaging',
                alias: '/dashboard/tracking/management/messaging',
                component: () => import('@/pages/dashboard/GlobalMessagingSettings.vue')
              },
              {
                path: 'team',
                name: 'app-dashboard-tracking-management-team',
                alias: '/dashboard/tracking/management/team',
                component: () => import('@/pages/dashboard/InternalTeamManagement.vue')
              },
              {
                path: 'domains',
                name: 'app-dashboard-tracking-management-domains',
                alias: '/dashboard/tracking/management/domains',
                component: () => import('@/pages/Domains/List.vue')
              },
              {
                path: 'domains/:uuid',
                name: 'app-dashboard-tracking-management-domain',
                alias: '/dashboard/tracking/management/domains/:uuid',
                component: () => import('@/pages/Domains/One.vue')
              },
              {
                path: 'versions',
                name: 'app-dashboard-tracking-management-versions',
                alias: '/dashboard/tracking/management/versions',
                beforeEnter: requireRoles(['admin']),
                component: () => import('@/pages/Versions/List.vue')
              }
            ]
          },
          {
            path: 'connections',
            redirect: { name: 'app-dashboard-tracking-elected-connections' }
          },
          {
            path: 'reads',
            redirect: { name: 'app-dashboard-tracking-employees-contents' }
          },
          {
            path: 'email-campaigns',
            redirect: { name: 'app-dashboard-tracking-elected-email-campaigns' }
          },
          {
            path: 'concerns',
            redirect: { name: 'app-dashboard-tracking-employees-concerns' }
          },
          {
            path: 'global-settings',
            redirect: { name: 'app-dashboard-tracking-management-messaging' }
          },
          {
            path: 'global-settings/messaging',
            redirect: { name: 'app-dashboard-tracking-management-messaging' }
          },
          {
            path: 'internal-team',
            redirect: { name: 'app-dashboard-tracking-management-team' }
          },
          {
            path: 'versions',
            redirect: { name: 'app-dashboard-tracking-management-versions' }
          }
        ]
      },
      {
        path: '/app/dashboard/tracking/connections',
        alias: '/dashboard/tracking/connections',
        redirect: { name: 'app-dashboard-tracking-elected-connections' }
      },
      {
        path: '/app/dashboard/tracking/reads',
        alias: '/dashboard/tracking/reads',
        redirect: { name: 'app-dashboard-tracking-employees-contents' }
      },
      {
        path: '/app/dashboard/tracking/email-campaigns',
        alias: '/dashboard/tracking/email-campaigns',
        redirect: { name: 'app-dashboard-tracking-elected-email-campaigns' }
      },
      {
        path: '/app/dashboard/tracking/published-contents',
        alias: '/dashboard/tracking/published-contents',
        redirect: { name: 'app-dashboard-tracking-elected-published-contents' }
      },
      {
        path: '/app/dashboard/connection-stats',
        alias: '/dashboard/connection-stats',
        redirect: { name: 'app-dashboard-tracking-elected-connections' }
      },
      {
        path: '/app/dashboard/tracking/versions',
        alias: '/dashboard/tracking/versions',
        redirect: { name: 'app-dashboard-tracking-management-versions' }
      },
      {
        path: '/app/dashboard/tracking/global-settings',
        alias: '/dashboard/tracking/global-settings',
        redirect: { name: 'app-dashboard-tracking-management-messaging' }
      },
      {
        path: '/app/dashboard/tracking/global-settings/messaging',
        alias: '/dashboard/tracking/global-settings/messaging',
        redirect: { name: 'app-dashboard-tracking-management-messaging' }
      },
      {
        path: '/app/dashboard/tracking/internal-team',
        alias: '/dashboard/tracking/internal-team',
        redirect: { name: 'app-dashboard-tracking-management-team' }
      },
      {
        path: '/app/dashboard/tracking/concerns',
        alias: '/dashboard/tracking/concerns',
        redirect: { name: 'app-dashboard-tracking-employees-concerns' }
      },
      {
        path: '/app/domains',
        name: 'app-domains',
        redirect: { name: 'app-dashboard-tracking-management-domains' }
      },
      {
        path: '/domains',
        redirect: { name: 'app-dashboard-tracking-management-domains' }
      },
      {
        path: '/app/domain/:uuid',
        name: 'app-domain',
        redirect: { name: 'app-dashboard-tracking-management-domain' }
      },
      {
        path: '/domain/:uuid',
        redirect: { name: 'app-dashboard-tracking-management-domain' }
      },
      {
        path: '/app/questions',
        name: 'app-questions',
        alias: '/questions',
        component: () => import('@/pages/Questions/List.vue')
      },
      {
        path: '/app/ai-assistant',
        name: 'app-ai-assistant',
        alias: '/ai-assistant',
        beforeEnter: requireRoles(['admin', 'community-manager', 'director', 'operator']),
        component: () => import('@/pages/AiAssistant/Home.vue')
      },
      {
        path: '/app/contents',
        name: 'app-contents',
        alias: '/contents',
        component: () => import('@/pages/Contents/List.vue')
      },
      {
        path: '/app/surveys',
        name: 'app-surveys',
        alias: '/surveys',
        component: () => import('@/pages/Surveys/List.vue')
      },
      {
        path: '/app/multisurveys',
        name: 'app-multisurveys',
        alias: '/multisurveys',
        component: () => import('@/pages/Surveys/MultiList.vue')
      },
      {
        path: '/app/surveys/media-mvp',
        name: 'app-surveys-media-mvp',
        alias: '/surveys-media-mvp',
        component: () => import ('@/pages/Surveys/MVPMedia.vue')
      },
      {
        path: '/app/surveys/:uuid',
        name: 'app-survey',
        alias: '/survey',
        component: () => import('@/pages/Surveys/One.vue')
      },
      {
        path: '/app/questions/universals',
        name: 'app-questions-universals',
        alias: '/questions-universals',
        component: () => import ('@/pages/Questions/Universals.vue')
      },
      {
        path: '/app/question/:uuid',
        name: 'app-question',
        alias: '/question',
        component: () => import('@/pages/Questions/One.vue')
      },
      {
        path: '/app/questions/parameters',
        name: 'app-questions-parameters',
        alias: '/questions-parameters',
        component: () => import('@/pages/Questions/Parameters.vue')
      },
      {
        path: '/app/articles',
        name: 'app-articles',
        alias: '/articles',
        component: () => import('@/pages/News/List.vue')
      },
      {
        path: '/app/articles/universals',
        name: 'app-articles-universals',
        alias: '/articles-universals',
        component: () => import ('@/pages/News/Universals.vue')
      },
      {
        path: '/app/articles/media-mvp',
        name: 'app-articles-media-mvp',
        alias: '/articles-media-mvp',
        component: () => import ('@/pages/News/MVPMedia.vue')
      },
      {
        path: '/app/articles/:uuid',
        name: 'app-article',
        alias: '/article',
        component: () => import('@/pages/News/List.vue')
      },
      {
        path: '/app/users',
        name: 'app-users',
        alias: '/users',
        beforeEnter: requireRoles(['admin', 'director', 'community-manager']),
        component: () => import('@/pages/Users/List.vue')
      },
      {
        path: '/app/users/:role',
        name: 'app-users-legacy',
        redirect: { name: 'app-users' }
      },
      {
        path: '/app/user/:uuid',
        name: 'app-user',
        alias: '/user',
        component: () => import('@/pages/Users/One.vue')
      },
      {
        path: '/app/chats',
        name: 'app-chats',
        alias: '/chats',
        component: () => import('@/pages/Chats/List.vue')
      },
      {
        path: '/app/chats/settings',
        name: 'app-chats-settings',
        alias: '/chats-settings',
        component: () => import('@/pages/Chats/Settings.vue')
      },
      {
        path: '/app/chats/:uuid',
        name: 'app-chats-chat',
        alias: '/chats-chat',
        component: () => import('@/pages/Chats/Chat.vue')
      },
      {
        path: '/app/requests/archives',
        name: 'app-requests-archives',
        alias: '/request-archives',
        component: () => import('@/pages/Requests/Archives/List.vue')
      },
      {
        path: '/app/requests/:uuid',
        name: 'app-requests-chat',
        alias: '/request-chat',
        component: () => import('@/pages/Requests/Chat.vue')
      },
      {
        path: '/app/parameters',
        name: 'app-parameters',
        alias: '/app-parameters',
        component: () => import('@/pages/Parameters/Home.vue')
      },
      {
        path: '/app/ai',
        name: 'app-ai-legacy',
        alias: '/ai',
        redirect: { name: 'app-dashboard-tracking-ai' }
      },
      // {
      //   path: '/app/documents',
      //   name: 'app-documents',
      //   alias: '/app-documents',
      //   component: () => import('@/pages/Documents/List.vue')
      // },
      {
        path: '/app/docs',
        name: 'app-events',
        alias: '/app-events',
        component: () => import('@/pages/Events/List.vue')
      },
      {
        path: '/app/groups',
        name: 'app-groups',
        alias: '/app-groups',
        redirect: { name: 'app-users' }
      },
      {
        path: '/app/groups/:uuid',
        name: 'app-group',
        alias: '/group',
        redirect: { name: 'app-users' }
      },
      {
        path: '/app/groups/:uuid/users',
        name: 'app-group-users',
        alias: '/group-users',
        redirect: { name: 'app-users' }
      },
      {
        path: '/app/versions',
        name: 'app-versions',
        alias: '/app-versions',
        redirect: { name: 'app-dashboard-tracking-management-versions' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

export default router
